import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { BOOKING_TIMEZONE, type AvailabilitySlot } from '../../../types';

interface BookingStepWhenProps {
  selectedDate: string;
  selectedSlot: AvailabilitySlot | null;
  slots: AvailabilitySlot[];
  loadingSlots: boolean;
  slotsError: string;
  loadingDays: boolean;
  disabledMatcher: (date: Date) => boolean;
  handleDateSelect: (date: Date | undefined) => void;
  handleSlotPick: (slot: AvailabilitySlot) => void;
  onClose: () => void;
  onContinue: () => void;
}

export function BookingStepWhen({
  selectedDate,
  selectedSlot,
  slots,
  loadingSlots,
  slotsError,
  loadingDays,
  disabledMatcher,
  handleDateSelect,
  handleSlotPick,
  onClose,
  onContinue,
}: BookingStepWhenProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.resolvedLanguage?.startsWith('ar') ?? false;

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const selectedDateObj = useMemo(() => {
    return selectedDate ? new Date(`${selectedDate}T00:00:00`) : undefined;
  }, [selectedDate]);

  return (
    <div className="booking-when">
      <div className="booking-when-grid">
        <div className="booking-card booking-card-date">
          <div className="booking-card-header booking-card-header-rich">
            <div className="booking-card-icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div>
              <h3 className="booking-card-title">{t('booking.form.date')}</h3>
              <p className="booking-card-hint">{t('booking.weekendNote')}</p>
            </div>
          </div>

          <DayPicker
            mode="single"
            selected={selectedDateObj}
            onSelect={handleDateSelect}
            disabled={disabledMatcher}
            startMonth={today}
            weekStartsOn={0}
            dir={isRtl ? 'rtl' : 'ltr'}
            className="booking-day-picker"
          />

          <div className="booking-date-footer">
            <div className="booking-date-footer-row">
              <span className="booking-date-footer-label">{t('booking.timezone')}</span>
              <span className="booking-date-footer-value">{BOOKING_TIMEZONE}</span>
            </div>
            {loadingDays && (
              <p className="booking-card-meta">{t('booking.loadingDays')}</p>
            )}
          </div>
        </div>

        <div className="booking-card booking-card-slots">
          <div className="booking-card-header booking-card-header-rich">
            <div className="booking-card-icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 7 12 12 15 14" />
              </svg>
            </div>
            <div>
              <h3 className="booking-card-title">{t('booking.form.slots')}</h3>
              {selectedDate ? (
                <p className="booking-card-hint">{selectedDate}</p>
              ) : (
                <p className="booking-card-hint">&nbsp;</p>
              )}
            </div>
          </div>

          {loadingSlots ? (
            <div className="booking-slot-grid">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="booking-slot booking-slot-skeleton" />
              ))}
            </div>
          ) : slotsError ? (
            <p className="booking-form-error">{slotsError}</p>
          ) : slots.length === 0 ? (
            <p className="booking-slots-empty">{t('booking.noSlots')}</p>
          ) : (
            <div className="booking-slot-grid" role="radiogroup" aria-label={t('booking.form.slots')}>
              {slots.map((slot) => {
                const isActive = selectedSlot?.startAt === slot.startAt;
                return (
                  <button
                    type="button"
                    key={slot.startAt}
                    role="radio"
                    aria-checked={isActive}
                    disabled={slot.booked}
                    className={`booking-slot${isActive ? ' is-active' : ''}${slot.booked ? ' is-booked' : ''}`}
                    onClick={() => handleSlotPick(slot)}
                    title={slot.booked ? t('booking.slotBooked') : undefined}
                  >
                    <span className="booking-slot-label">{slot.label}</span>
                    {slot.booked && (
                      <span className="booking-slot-badge" aria-hidden>
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="booking-modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>
          {t('booking.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-filled"
          onClick={onContinue}
          disabled={!selectedSlot}
        >
          {t('booking.continue')}
        </button>
      </div>
    </div>
  );
}
